import type { DiplomacyRecord, GameState } from "../shared/game-types";
import { clamp, round } from "./core";

/** Diplomacy appears only after a real interstellar survey confirms an inhabited system. */
export function isDiplomacyUnlocked(state: Pick<GameState, "systems" | "civilizations">): boolean {
  if (state.civilizations.some((civilization) => civilization.contacted)) return true;
  return state.civilizations.some((civilization) => {
    if (!civilization.known) return false;
    const homeSystem = state.systems.find((system) => system.id === civilization.homeSystemId);
    return Boolean(homeSystem?.observed && homeSystem.distanceLy > 0);
  });
}

function peacefulStatus(record: DiplomacyRecord): DiplomacyRecord["status"] {
  if (record.affinity < 45) return "cold";
  if (record.affinity < 60) return "neutral";
  if (record.affinity < 75) return "friendly";
  return "partner";
}

/** Slow NPC relationship drift; trade helps, unresolved border tension hurts. */
export function advanceDiplomacy(state: GameState, deltaYears: number): void {
  if (!(deltaYears > 0)) return;
  for (const civilization of state.civilizations) {
    if (!civilization.contacted) continue;
    const record = state.diplomacy[civilization.id];
    if (!record || record.status === "war") continue;
    const activeTrades = state.trades.filter((trade) => trade.civilizationId === civilization.id && trade.status === "active").length;
    const cooperative = civilization.personality.includes("cooperative") || civilization.personality.includes("longTerm") ? 0.06 : 0;
    const suspicious = civilization.personality.includes("suspicious") || civilization.personality.includes("isolationist") ? 0.045 : 0;
    const borderGrowth = civilization.personality.includes("expansionist") ? 0.08 : -0.025;
    record.borderTension = round(clamp(record.borderTension + borderGrowth * deltaYears, 0, 100));
    const drift = (activeTrades * 0.12 + cooperative - suspicious - record.borderTension * 0.0015) * deltaYears;
    record.affinity = round(clamp(record.affinity + drift, 0, 100));
    record.status = peacefulStatus(record);
  }
}
